import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import {
  SupportTicket,
  SupportTicketListFilters,
  SupportTicketStatus,
  SupportTicketTopic,
} from '../../core/models/api.models';

@Component({
  selector: 'app-support-tickets',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink],
  templateUrl: './support-tickets.component.html',
  styleUrl: './support-tickets.component.scss',
})
export class SupportTicketsComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly rows = signal<SupportTicket[]>([]);
  readonly page = signal(1);
  readonly limit = signal(20);
  readonly total = signal(0);

  readonly statuses: SupportTicketStatus[] = [
    'pending',
    'in_progress',
    'resolved',
    'closed',
  ];

  readonly topics: { key: SupportTicketTopic; label: string }[] = [
    { key: 'account', label: 'Account' },
    { key: 'billing_subscription', label: 'Billing & subscription' },
    { key: 'payments_transfers', label: 'Payments & transfers' },
    { key: 'cards', label: 'Cards' },
    { key: 'rent', label: 'Rent' },
    { key: 'rewards_points', label: 'Rewards & points' },
    { key: 'technical', label: 'Technical issue' },
    { key: 'other', label: 'Other' },
  ];

  status = '';
  topic = '';
  search = '';

  ngOnInit(): void {
    this.load();
  }

  private filters(page = this.page()): SupportTicketListFilters {
    return {
      page,
      limit: this.limit(),
      status: this.status || undefined,
      topic: this.topic || undefined,
      search: this.search.trim() || undefined,
    };
  }

  load(page = this.page()): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getSupportTickets(this.filters(page)).subscribe({
      next: (data) => {
        this.rows.set(data.rows || []);
        this.page.set(data.page);
        this.limit.set(data.limit);
        this.total.set(data.total);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load support tickets');
        this.loading.set(false);
      },
    });
  }

  applyFilters(): void {
    this.load(1);
  }

  clearFilters(): void {
    this.status = '';
    this.topic = '';
    this.search = '';
    this.load(1);
  }

  userLabel(row: SupportTicket): string {
    const u = row.user;
    if (!u) return `User #${row.user_id}`;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return name || u.email || `User #${row.user_id}`;
  }

  previewMessage(message: string | null | undefined): string {
    const text = String(message || '').trim();
    if (text.length <= 80) return text || '—';
    return `${text.slice(0, 80)}…`;
  }

  statusLabel(status: string | null | undefined): string {
    if (!status) return '—';
    return status.replace(/_/g, ' ');
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit()));
  }

  prev(): void {
    if (this.page() > 1) this.load(this.page() - 1);
  }

  next(): void {
    if (this.page() < this.totalPages()) this.load(this.page() + 1);
  }
}
